/* eslint-disable no-nested-ternary */
import { initialTranslations } from '@app/constants';
import i18n from '@app/i18n';
import { isTranslationsEmpty } from '@app/utils';
import {
  QuestionContextTranslationView,
  QuestionDetailsView,
  QuestionTranslationView,
  QuestionType,
  QuestionUpdateBody,
  TranslationBody,
  VisibilityType,
} from '@data/sdk/match';
import { Language } from '@data/utils/enums';
import { QuestionFormValues } from '../hooks';

const getQuestionTranslations = (translations: QuestionTranslationView[]) =>
  translations.reduce((acc: { [key in Language]: string }, el: QuestionTranslationView) => {
    acc[el.language as Language] = el.value;
    return acc;
  }, {} as { [key in Language]: string });

export const getInitialValues = (
  question: QuestionDetailsView | undefined,
  translations: QuestionTranslationView[] | undefined,
  contextTranslations: QuestionContextTranslationView[] | undefined,
) => ({
  autoTranslateText: true,
  unit: question?.unit || '',
  questionType: question?.questionType || QuestionType.Standard,
  visibilityType: question?.visibilityType || VisibilityType.Default,
  text: translations ? getQuestionTranslations(translations) : initialTranslations,
  context: contextTranslations ? getQuestionTranslations(contextTranslations) : initialTranslations,
  expiry: question?.expiry || null,
});

export const getQuestionVisibilityOptions = () => [
  { label: i18n.t('admin:question_visibility.default'), value: VisibilityType.Default },
  { label: i18n.t('admin:question_visibility.first'), value: VisibilityType.First },
  { label: i18n.t('admin:question_visibility.hidden'), value: VisibilityType.Hidden },
];

export const transformQuestionFormValues = (
  formValues: QuestionFormValues,
  initialValues: QuestionFormValues,
  options?: {
    autoTranslateText?: boolean;
  },
): {
  contextValues: TranslationBody[];
  isContextEmpty: boolean;
  questionValues: Partial<QuestionUpdateBody>;
  translationsValues: TranslationBody[];
} => {
  const isContextAdded = !initialValues.context[Language.EN] && !!formValues.context[Language.EN];
  const isContextRemoved = !!initialValues.context[Language.EN] && !formValues.context[Language.EN];
  const isContextEmpty = isTranslationsEmpty(formValues.context);

  const questionValues: Partial<QuestionUpdateBody> = {
    unit: formValues.unit,
    visibilityType: formValues.visibilityType,
    questionType: formValues.questionType,
    expiry: formValues.expiry,
    context:
      isContextRemoved && isContextEmpty
        ? null
        : isContextAdded
        ? formValues.context[Language.EN]
        : undefined,
  };

  const enText = formValues.text[Language.EN];

  const translationsValues = Object.values(Language).reduce<TranslationBody[]>((acc, language) => {
    const isAutoTranslateEnabled = !!options?.autoTranslateText;
    const shouldAutoGenerate = isAutoTranslateEnabled
      ? language !== Language.EN
      : !formValues.text[language];

    const value = shouldAutoGenerate ? enText : formValues.text[language] || enText;

    acc.push({
      language,
      value,
      autoGenerate: shouldAutoGenerate,
    });

    return acc;
  }, []);

  const enContext = formValues.context[Language.EN];

  const contextValues = Object.values(Language).reduce<TranslationBody[]>((acc, language) => {
    const shouldAutoGenerate = !formValues.context[language];

    const value = shouldAutoGenerate ? enContext : formValues.context[language] || enContext;

    acc.push({
      language,
      value,
      autoGenerate: shouldAutoGenerate,
    });

    return acc;
  }, []);

  return { questionValues, translationsValues, contextValues, isContextEmpty };
};
